import { createContext, ReactNode } from "react";
import { ActiveAccount, BaseRiskReportParams, Portfolio, PortfolioKeyLimitAlert, ReportReceiver, ReportReceiverMode, ReportSetting, RiskDesk, SendDeskPositionReportDepthLevelType } from "src/@type/risk-management/types";
import { DeskPositionData, GroupDesk } from "src/@type/risk-management/desk-position";
import { GlobalPositionData } from "src/@type/risk-management/global-position";
import { ActiveAccountDTO, DeskPositionDataDTO, FocusReportDataDTO, GlobalPositionDataDTO, GreeksReportDataDTO, GroupDeskDTO, PortfolioDTO, PortfolioKeyLimitAlertDTO, PositionByDataDTO, ReportReceiverDTO, ReportSettingDTO, RiskDeskDTO } from "./DTO/DTO";
import { mapperActiveAccountDTOToModel, mapperDeskPositionDataDTOToModel, mapperFocusReportDataDTOToModel, mapperGlobalPositionDataDTOToModel, mapperGreeksReportDataDTOToModel, mapperGroupDeskDTOToModel, mapperPortfolioDTOToModel, mapperPortfolioKeyLimitAlertDTOToModel, mapperPositionByDataDTOToModel, mapperReportReceiverDTOToModel, mapperReportSettingDTOToModel, mapperRiskDeskDTOToModel } from "./DTO/DTOToModel";
import { getInvariantDateString } from "src/utils/formatTime";
import { mapperPortfolioKeyLimitAlertToDTO, mapperPortfolioToDTO, mapperRiskDeskModelToDTO } from "./DTO/ModelToDTO";
import { FocusReportData } from "src/@type/risk-management/focus-report";
import { PositionByData } from "src/@type/risk-management/position-by";
import { GreeksReportData } from "src/@type/risk-management/greeks-report";
import { useAddDelDataContext, useBaseDataContext, useGeneralCRUDDataContext }  from "../hooks";
import { SendGeneralReportParams } from "./utils/type";
import useRiskReport from "./utils/useRiskReport";
import { DynamicPrototypeReportData } from "src/@type/risk-management/dynamic-prototype";
const ROUTE = 'risk-management';

type SendPositionReportParams = { riskDeskId: number, depthLevel: SendDeskPositionReportDepthLevelType } & SendGeneralReportParams

type PortfolioAccountParams = { portfolioId: number, account: string }
type DeskReportReceiverAddParams = { deskId: number, email: string, mode: ReportReceiverMode }
type DeskReportReceiverDelParams = { deskId: number, email: string }
type ReportReceiverAddParams = { reportId: number, email: string, mode: ReportReceiverMode }
type ReportReceiverDelParams = { reportId: number, email: string }

type RiskManagementDataContextType = {
    getActiveAccounts: (all?: boolean) => Promise<ActiveAccount[]>;
    getGroupDesks: () => Promise<GroupDesk[]>
    getRiskDesk: (riskDeskId: number) => Promise<RiskDesk>
    getDynamicPrototypeReportData: (params: BaseRiskReportParams) => Promise<DynamicPrototypeReportData>
    getGlobalPositionReportData: (params: BaseRiskReportParams) => Promise<GlobalPositionData>
    getDeskPositionReportData: (riskDeskId: number, date: Date) => Promise<DeskPositionData>
    getFocusReportData: (params: BaseRiskReportParams) => Promise<FocusReportData>
    getPositionByDeskData: (params: BaseRiskReportParams) => Promise<PositionByData>
    getPositionByProductData: (params: BaseRiskReportParams) => Promise<PositionByData>
    getGreeksReportData: (params: BaseRiskReportParams) => Promise<GreeksReportData>
    getRiskDesks: (onlyActive?: boolean) => Promise<RiskDesk[]>
    getReportReceivers: () => Promise<ReportReceiver[]>
    postRiskDesk: (dl: Partial<RiskDesk>) => Promise<RiskDesk>
    putRiskDesk: (dl: Partial<RiskDesk>) => Promise<RiskDesk>
    deleteRiskDesk: (dl: Partial<RiskDesk>) => Promise<boolean>
    sendDeskPositionReport: ({ riskDeskId, date, sendAnyway, depthLevel, to, cc, bcc } :SendPositionReportParams) => Promise<void>
    sendAllDeskPositionReports: (date: Date) => Promise<void>
    sendGlobalReportReport: (params :SendGeneralReportParams) => Promise<void>
    sendFocusReportReport: (params :SendGeneralReportParams) => Promise<void>
    sendPositionByDeskReport: (params :SendGeneralReportParams) => Promise<void>
    sendPositionByProductReport: (params :SendGeneralReportParams) => Promise<void>
    sendGreeksReportReport: (params :SendGeneralReportParams) => Promise<void>
    getPortfolioKeyLimitAlerts: () => Promise<PortfolioKeyLimitAlert[]>
    getPortfolioKeyLimitAlert: (portfolioId: number) => Promise<PortfolioKeyLimitAlert>
    postPortfolioKeyLimitAlert: (portfolio: Partial<PortfolioKeyLimitAlert>) => Promise<PortfolioKeyLimitAlert>
    putPortfolioKeyLimitAlert: (portfolio: Partial<PortfolioKeyLimitAlert>) => Promise<PortfolioKeyLimitAlert>
    deletePortfolioKeyLimitAlert: (portfolio: Partial<PortfolioKeyLimitAlert>) => Promise<boolean>
    getPortfolios: () => Promise<Portfolio[]>
    getPortfolio: (limitId: number) => Promise<Portfolio>
    postPortfolio: (limit: Partial<Portfolio>) => Promise<Portfolio>
    putPortfolio: (limit: Partial<Portfolio>) => Promise<Portfolio>
    deletePortfolio: (limit: Partial<Portfolio>) => Promise<boolean>
    addPortfolioAccount: (params: PortfolioAccountParams) => Promise<boolean>
    delPortfolioAccount: (params: PortfolioAccountParams) => Promise<boolean>
    addDeskReportReceiver: (params: DeskReportReceiverAddParams) => Promise<boolean>
    delDeskReportReceiver: (params: DeskReportReceiverDelParams) => Promise<boolean>
    getReportSettings: () => Promise<ReportSetting[]>
    addReportReceiver: (params: ReportReceiverAddParams) => Promise<boolean>, 
    delReportReceiver: (params: ReportReceiverDelParams) => Promise<boolean>,
};

export const RiskManagementDataContext = createContext<RiskManagementDataContextType | null>(null);

type RiskManagementDataContextProviderProps = {
    children: ReactNode;
};

export function RiskManagementDataContextProvider({ children }: RiskManagementDataContextProviderProps) {
    const {
        getBaseResult,
        getBaseResultArray,
        actionBaseResult
    } = useBaseDataContext(ROUTE);

    const getActiveAccounts = (all: boolean = true) => getBaseResultArray<ActiveAccount, ActiveAccountDTO>({
        controller: 'activeAccounts', mapperDTOToModel: mapperActiveAccountDTOToModel, params: { all }
    });

    const getGroupDesks = () => getBaseResultArray<GroupDesk, GroupDeskDTO>({
        controller: 'groupDesks', mapperDTOToModel: mapperGroupDeskDTOToModel
    });

    const getReportReceivers = () => getBaseResultArray<ReportReceiver, ReportReceiverDTO>({
        controller: 'reportReceivers', mapperDTOToModel: mapperReportReceiverDTOToModel
    });

    const getReportSettings = () => getBaseResultArray<ReportSetting, ReportSettingDTO>({
        controller: 'reportSettings', mapperDTOToModel: mapperReportSettingDTOToModel
    });

    /* Risk Report */
    const getDynamicPrototypeReportData = ({ date, ...others }: BaseRiskReportParams) => getBaseResult<GlobalPositionData, GlobalPositionDataDTO>({ 
        controller: 'globalPositionReport', 
        params: { date: getInvariantDateString(date), ...others },
         mapperDTOToModel: mapperGlobalPositionDataDTOToModel
    });

    const globalPositionReportActions = (({ getReportData, sendReport }) => ({
        getGlobalPositionReportData: getReportData, sendGlobalReportReport: sendReport
    }))(useRiskReport<GlobalPositionData, GlobalPositionDataDTO, BaseRiskReportParams>({
        route: ROUTE, controller: 'globalPositionReport', mapperDTOToModel: mapperGlobalPositionDataDTOToModel
    }));

    const getDeskPositionReportData = (riskDeskId: number, date: Date) => getBaseResult<DeskPositionData, DeskPositionDataDTO>({ 
        controller: 'deskPositionReport', 
        params: {
            riskDeskId: riskDeskId,
            date: getInvariantDateString(date)
        }, 
        mapperDTOToModel: mapperDeskPositionDataDTOToModel 
    });

    const focusReportActions = (({ getReportData, sendReport }) => ({
        getFocusReportData: getReportData, sendFocusReportReport: sendReport
    }))(useRiskReport<FocusReportData, FocusReportDataDTO, BaseRiskReportParams>({
        route: ROUTE, controller: 'focusReport', mapperDTOToModel: mapperFocusReportDataDTOToModel
    }));

    const positionByDeskActions = (({ getReportData, sendReport }) => ({
        getPositionByDeskData: getReportData, sendPositionByDeskReport: sendReport
    }))(useRiskReport<PositionByData, PositionByDataDTO, BaseRiskReportParams>({
        route: ROUTE, controller: 'positionByDeskReport', mapperDTOToModel: mapperPositionByDataDTOToModel
    }));

    const positionByProductActions = (({ getReportData, sendReport }) => ({
        getPositionByProductData: getReportData, sendPositionByProductReport: sendReport
    }))(useRiskReport<PositionByData, PositionByDataDTO, BaseRiskReportParams>({
        route: ROUTE, controller: 'positionByProductReport', mapperDTOToModel: mapperPositionByDataDTOToModel
    }));

    const greeksReportActions = (({ getReportData, sendReport }) => ({
        getGreeksReportData: getReportData, sendGreeksReportReport: sendReport
    }))(useRiskReport<GreeksReportData, GreeksReportDataDTO, BaseRiskReportParams>({
        route: ROUTE, controller: 'greeksReport', mapperDTOToModel: mapperGreeksReportDataDTOToModel
    }));

    const sendDeskPositionReport = (params: SendPositionReportParams) => actionBaseResult({
        controller: 'deskPositionReport',
        action: 'send',
        params: params
    });

    const sendAllDeskPositionReports = (date: Date) => actionBaseResult({
        controller: 'deskPositionReport',
        action: 'sendAll',
        params: { date: getInvariantDateString(date) }
    });

    /* RiskDesk CRUD */
    const riskDeskCRUD = (({ getAll, get, post, put, del }) => ({
        getRiskDesks: (onlyActive: boolean = true) => getAll({ onlyActive }),
        getRiskDesk: get,
        postRiskDesk: post,
        putRiskDesk: put,
        deleteRiskDesk: del
    }))(useGeneralCRUDDataContext<RiskDesk, RiskDeskDTO>({
        route: ROUTE,
        controller: 'riskDesk',
        mapperModelToDTO: mapperRiskDeskModelToDTO,
        mapperDTOToModel: mapperRiskDeskDTOToModel
    }));

    /* portfolioKeyLimitAlert CRUD */
    const portfolioKeyLimitAlertCRUD = (({ getAll, get, post, put, del }) => ({
        getPortfolioKeyLimitAlerts: getAll,
        getPortfolioKeyLimitAlert: get,
        postPortfolioKeyLimitAlert: post,
        putPortfolioKeyLimitAlert: put,
        deletePortfolioKeyLimitAlert: del
    }))(useGeneralCRUDDataContext<PortfolioKeyLimitAlert, PortfolioKeyLimitAlertDTO>({
        route: ROUTE,
        controller: 'portfolioKeyLimitAlert',
        mapperModelToDTO: mapperPortfolioKeyLimitAlertToDTO,
        mapperDTOToModel: mapperPortfolioKeyLimitAlertDTOToModel
    }));

    /* portfolio CRUD */
    const portfolioCRUD = (({ getAll, get, post, put, del }) => ({
        getPortfolios: getAll,
        getPortfolio: get,
        postPortfolio: post,
        putPortfolio: put,
        deletePortfolio: del
    }))(useGeneralCRUDDataContext<Portfolio, PortfolioDTO>({
        route: ROUTE,
        controller: 'portfolio',
        mapperModelToDTO: mapperPortfolioToDTO,
        mapperDTOToModel: mapperPortfolioDTOToModel
    }));

    /* portfolioAccount ADD DEL */
    const portfolioAccountAddDel = (({ add, del }) => ({
        addPortfolioAccount: add, delPortfolioAccount: del
    }))(useAddDelDataContext<PortfolioAccountParams, PortfolioAccountParams>({ 
        route: ROUTE, controller: 'portfolioAccount' 
    }));
    
    /* deskReportReceiver ADD DEL */
    const deskReportReceiverAddDel = (({ add, del }) => ({
        addDeskReportReceiver: add, delDeskReportReceiver: del
    }))(useAddDelDataContext<DeskReportReceiverAddParams, DeskReportReceiverDelParams>({ 
        route: ROUTE, controller: 'deskReportReceiver' 
    }));

     /* deskReportReceiver ADD DEL */
    const reportReceiverAddDel = (({ add, del }) => ({
        addReportReceiver: add, delReportReceiver: del
    }))(useAddDelDataContext<ReportReceiverAddParams, ReportReceiverDelParams>({ 
        route: ROUTE, controller: 'generalReportReceiver' 
    }));

    return (
        <RiskManagementDataContext.Provider
            value={{
                getActiveAccounts,
                getGroupDesks,
                getReportReceivers,
                getReportSettings, 
                getDynamicPrototypeReportData,
                ...globalPositionReportActions,
                getDeskPositionReportData, sendDeskPositionReport,
                ...focusReportActions,
                ...positionByDeskActions,
                ...positionByProductActions,
                ...greeksReportActions,
                sendAllDeskPositionReports,
                ...riskDeskCRUD,
                ...portfolioKeyLimitAlertCRUD,
                ...portfolioCRUD,
                ...portfolioAccountAddDel,
                ...deskReportReceiverAddDel,
                ...reportReceiverAddDel
            }}
        >
            {children}
        </RiskManagementDataContext.Provider>
    );
}